import { TypedDocumentNode, gql } from '@apollo/client';

import {
  CardRarity,
  IRLPrize,
  CardRarity as Rarity,
  RewardState,
  SupportedCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { isType, withFragments } from '@sorare/core/src/gql';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';
import { sortByRarity } from '@sorare/core/src/lib/scarcity';

import { findIRLPrizeForRank } from 'lib/sports';

import {
  groupLineupRewardsByType_BoxReward,
  groupLineupRewardsByType_CardReward,
  groupLineupRewardsByType_CashReward,
  groupLineupRewardsByType_ConversionCreditReward,
  groupLineupRewardsByType_LeaderboardPrizePool,
  groupLineupRewardsByType_LineupInterface,
  groupLineupRewardsByType_ProjectedReward,
} from './__generated__/groupLineupRewardsByType.graphql';

const { USD } = SupportedCurrency;

type LineupRewardsByType = {
  irlPrize: Array<IRLPrize>;
  cards: Array<{
    rarity: Rarity;
    tier: number;
    slug?: string;
    name?: string;
  }>;
  cash: Array<{
    amountCents: number;
    currency: SupportedCurrency;
  }>;
  conversionCredits: Array<{
    amountCents: number;
    currency: SupportedCurrency;
  }>;
  boxes: number;
};

const LineupRewardFragments = {
  CashReward: gql`
    fragment groupLineupRewardsByType_CashReward on CashReward {
      id
      amountUSDCents
    }
  ` as TypedDocumentNode<groupLineupRewardsByType_CashReward>,
  ConversionCreditReward: gql`
    fragment groupLineupRewardsByType_ConversionCreditReward on ConversionCreditReward {
      id
      monetaryAmount {
        ...MonetaryAmountFragment_monetaryAmount
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<groupLineupRewardsByType_ConversionCreditReward>,
  CardReward: gql`
    fragment groupLineupRewardsByType_CardReward on CardReward {
      id
      tier
      card {
        slug
        rarityTyped
        anyPlayer {
          slug
          displayName
        }
      }
      state
    }
  ` as TypedDocumentNode<groupLineupRewardsByType_CardReward>,
  BoxReward: gql`
    fragment groupLineupRewardsByType_BoxReward on BoxReward {
      id
      name
      pictureUrl
      state
    }
  ` as TypedDocumentNode<groupLineupRewardsByType_BoxReward>,
};

const initializeLineupRewardsByType = (): LineupRewardsByType => ({
  irlPrize: [],
  cards: [],
  cash: [],
  conversionCredits: [],
  boxes: 0,
});

export const groupLineupRewardsByType = withFragments(
  (
    lineup?:
      | (groupLineupRewardsByType_LineupInterface & {
          projectedReward?: groupLineupRewardsByType_ProjectedReward | null;
        })
      | null,
    prizePool?: groupLineupRewardsByType_LeaderboardPrizePool
  ): LineupRewardsByType => {
    let result = initializeLineupRewardsByType();

    if (!lineup) return result;

    const { lineupRewards, projectedReward } = lineup;

    if (prizePool) {
      const irlPrize = findIRLPrizeForRank(prizePool, lineup.rank);
      if (irlPrize) {
        result.irlPrize.push(irlPrize);
      }
    }

    if (projectedReward) {
      const { cards, amountUSDCents, amountUSDCentsConversionCredit } =
        projectedReward;

      result.cards = sortByRarity(cards, ({ rarity }) => rarity).map(
        ({ rarity, tier }) => ({ rarity, tier })
      );

      if (amountUSDCents > 0) {
        result.cash.push({ amountCents: amountUSDCents, currency: USD });
      }

      if (amountUSDCentsConversionCredit > 0) {
        result.conversionCredits.push({
          amountCents: amountUSDCentsConversionCredit,
          currency: USD,
        });
      }

      return result;
    }

    result = lineupRewards.reduce<LineupRewardsByType>((acc, reward) => {
      if (isType(reward, 'CardReward')) {
        const { card, tier, state } = reward;
        const { slug, rarityTyped, anyPlayer } = card;
        const { displayName: name } = anyPlayer;
        const claimed = state === RewardState.CLAIMED;
        acc.cards.push({
          ...(claimed ? { slug, name } : {}),
          rarity: rarityTyped as unknown as CardRarity,
          tier,
        });
      } else if (isType(reward, 'CashReward')) {
        const { amountUSDCents } = reward;
        acc.cash.push({ amountCents: amountUSDCents, currency: USD });
      } else if (isType(reward, 'ConversionCreditReward')) {
        const { monetaryAmount } = reward;
        acc.conversionCredits.push({
          amountCents: Number(monetaryAmount.usd),
          currency: USD,
        });
      } else if (isType(reward, 'BoxReward')) {
        acc.boxes += 1;
      }
      return acc;
    }, result);

    result.cards = sortByRarity(result.cards, ({ rarity }) => rarity);

    return result;
  },
  {
    LeaderboardPrizePool: gql`
      fragment groupLineupRewardsByType_LeaderboardPrizePool on LeaderboardPrizePool {
        ...findIRLPrizeForRank_LeaderboardPrizePool
      }
      ${findIRLPrizeForRank.fragments.LeaderboardPrizePool}
    ` as TypedDocumentNode<groupLineupRewardsByType_LeaderboardPrizePool>,
    ProjectedReward: gql`
      fragment groupLineupRewardsByType_ProjectedReward on ProjectedReward {
        cards {
          tier
          rarity
        }
        amountUSDCents
        amountUSDCentsConversionCredit
      }
    ` as TypedDocumentNode<groupLineupRewardsByType_ProjectedReward>,
    LineupInterface: gql`
      fragment groupLineupRewardsByType_LineupInterface on LineupInterface {
        id
        rank
        lineupRewards {
          ... on CashReward {
            id
            ...groupLineupRewardsByType_CashReward
          }
          ... on ConversionCreditReward {
            id
            ...groupLineupRewardsByType_ConversionCreditReward
          }
          ... on CardReward {
            id
            ...groupLineupRewardsByType_CardReward
          }
          ... on BoxReward {
            id
            ...groupLineupRewardsByType_BoxReward
          }
        }
      }
      ${LineupRewardFragments.CashReward}
      ${LineupRewardFragments.ConversionCreditReward}
      ${LineupRewardFragments.CardReward}
      ${LineupRewardFragments.BoxReward}
    ` as TypedDocumentNode<groupLineupRewardsByType_LineupInterface>,
  }
);
